import dayjs from 'dayjs';
import BasicDatePicker from '@components/Picker/BasicDatePicker';
import SelectField from '@components/TextField/SelectField';
import RadioField from '@components/Select/Radio';
import reportIcon from '../../../assets/images/report-icon.png';
import listIcon from '../../../assets/images/listIcon.png';
import networkIcon from '../../../assets/images/networkIcon.png';
import editDocumentIcon from '../../../assets/images/editDocumentIcon.png';

import { Spin } from 'antd';
import LabelWithMultipleSelect from '@components/LabelWithMultipleSelect';

export const REPORT_TYPES = {
    daily: 'daily',
    compact: 'compact',
    grouped: 'grouped',
    summary: 'summary',
};

export const generateReportInputs = ({
    propertyOptions = [],
    incidentsOptions = [],
    userOptions = [],
    reportType = REPORT_TYPES.daily,
    isLevelDisabled,
    dateFormat = 'MM/DD/YYYY',
    onPropScroll,
    propLoading,
}) => {
    const inputs = {
        startDate: {
            name: 'dates.startDate',
            label: 'Start Date',
            component: BasicDatePicker,
            format: dateFormat,
            disabledDate: (current) => {
                let customDate = dayjs().format('YYYY-MM-DD');
                return current && current > dayjs(customDate, 'YYYY-MM-DD');
            },
            transform: {
                value: (value) => dayjs(value),
                change: (value) => value.format('YYYY-MM-DD'),
            },
            triggerKey: 'dates',
        },
        endDate: {
            name: 'dates.endDate',
            label: 'End Date',
            component: BasicDatePicker,
            format: dateFormat,
            disabledDate: (current) => {
                let customDate = dayjs().format('YYYY-MM-DD');
                return current && current > dayjs(customDate, 'YYYY-MM-DD');
            },
            transform: {
                value: (value) => dayjs(value),
                change: (value) => value.format('YYYY-MM-DD'),
            },
            triggerKey: 'dates',
        },
        propertyId: {
            name: 'propertyId',
            label: 'Choose Property',
            placeholder: 'Select from list',
            component: SelectField,
            options: propertyOptions,
            onPopupScroll: onPropScroll,
            dropdownRender: (menu) => (
                <>
                    {menu}
                    {propLoading && <div style={{ textAlign: 'center', padding: '8px 0' }}><Spin size="small" /></div>}
                </>
            ),
            transform: {
                value: (value) => value?.[0],
                change: (value) => [value],
            },
        },
        properties: {
            name: 'propertyId',
            label: 'Choose Properties',
            placeholder: 'Select from list',
            component: LabelWithMultipleSelect,
            wrapperClassName: 'py-0',
            options: propertyOptions,
            onPopupScroll: onPropScroll,
            dropdownRender: (menu) => (
                <>
                    {menu}
                    {propLoading && <div style={{ textAlign: 'center', padding: '8px 0' }}><Spin size="small" /></div>}
                </>
            ),
        },
        incidentTypeId: {
            name: 'incidentTypeId',
            label: 'Issue Type',
            placeholder: 'Select from list',
            component: LabelWithMultipleSelect,
            wrapperClassName: 'py-0',
            options: incidentsOptions,
            transform: {
                value: (value) => value,
                change: (value) => value,
            },
        },
        incidentTypes: {
            name: 'incidentTypeId',
            label: 'Issue Types',
            placeholder: 'Select from list',
            component: LabelWithMultipleSelect,
            wrapperClassName: 'py-0',
            options: incidentsOptions,
        },
        level: {
            name: 'level',
            type: 'radio',
            component: RadioField,
            disabled: isLevelDisabled,
            options: [
                {
                    label: 'High',
                    value: 3,
                },
                {
                    label: 'Medium',
                    value: 2,
                },
                {
                    label: 'Low',
                    value: 1,
                },
                {
                    label: 'All',
                    value: 0,
                },
            ],
            span: 1,
        },
        status: {
            name: 'status',
            type: 'radio',
            component: RadioField,
            options: [
                {
                    label: 'Pending',
                    value: 'pending',
                },
                {
                    label: 'Assigned',
                    value: 'assigned',
                },
            ],
            span: 1,
        },
        assignee: {
            name: 'assignee',
            label: 'Assignee Name',
            labelClass: 'font-normal',
            placeholder: 'Select from list',
            component: SelectField,
            wrapperClassName: 'py-0',
            options: userOptions,
        },
        status: {
            name: 'status',
            type: 'radio',
            component: RadioField,
            options: [
                {
                    label: 'Pending',
                    value: 'pending',
                },
                {
                    label: 'Assigned',
                    value: 'assigned',
                },
            ],
            span: 1,
        },
    };

    const reportInputs = {
        [REPORT_TYPES.daily]: [
            inputs.startDate,
            inputs.endDate,
            inputs.propertyId,
            inputs.incidentTypeId,
            inputs.status,
            inputs.level
        ],
        [REPORT_TYPES.compact]: [
            inputs.startDate,
            inputs.endDate,
            inputs.propertyId,
            inputs.status,
        ],
        [REPORT_TYPES.grouped]: [
            inputs.startDate,
            inputs.endDate,
            inputs.properties,
            inputs.incidentTypes,
            inputs.assignee,
            inputs.status,
        ],
        [REPORT_TYPES.summary]: [
            inputs.startDate,
            inputs.endDate,
            inputs.properties,
            inputs.incidentTypeId,
            inputs.status,
            inputs.level,
        ],
    };

    return reportInputs[reportType] || [];
};

export const generateReportDefaults = {
    dates: {
        startDate: dayjs().format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
    },
    propertyId: undefined,
    incidentTypeId: undefined,
};

export const generateReportContent = ({ reportType }) => {
    const contentMap = {
        [REPORT_TYPES.daily]: {
            title: 'Daily Activity Report',
        },
        [REPORT_TYPES.compact]: {
            title: 'Compact Activity Report',
        },
        [REPORT_TYPES.grouped]: {
            title: 'Grouped Activity Report',
        },
        [REPORT_TYPES.summary]: {
            title: 'Summary Report',
        },
    };

    return contentMap[reportType] || {};
};

export const getReportTypes = ({ onOpenDrawer }) => [
    {
        label: 'Daily Reports',
        icon: reportIcon,
        onClick: () => onOpenDrawer(REPORT_TYPES.daily),
    },
    {
        label: 'Compact Activity',
        icon: listIcon,
        onClick: () => onOpenDrawer(REPORT_TYPES.compact),
    },
    {
        label: 'Grouped Activity',
        icon: networkIcon,
        onClick: () => onOpenDrawer(REPORT_TYPES.grouped),
    },
    {
        label: 'Report Summary',
        icon: editDocumentIcon,
        onClick: () => onOpenDrawer(REPORT_TYPES.summary),
    },
];
